import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { apiClient, getAuthToken, setAuthToken } from "@/lib/apiClient";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      if (getAuthToken()) {
        try {
          setUser(await apiClient.auth.me());
        } catch {
          // Invalid/expired token — clear it and continue as a guest.
          setAuthToken(null);
        }
      }
      setLoading(false);
    })();
    // Restore the session once on mount; apiClient/get/setAuthToken are stable module refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (creds) => {
    const r = await apiClient.auth.login(creds);
    setUser(r.user);
    return r.user;
  }, []);

  const register = useCallback(async (creds) => {
    const r = await apiClient.auth.register(creds);
    setUser(r.user);
    return r.user;
  }, []);

  const logout = useCallback(() => {
    apiClient.auth.logout();
    setUser(null);
  }, []);

  // Memoized so the context value keeps a stable reference between renders.
  const value = useMemo(
    () => ({ user, loading, login, register, logout }),
    [user, loading, login, register, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
export default AuthContext;
